import { useAtom } from "jotai";
import { Building2, Loader2, Store } from "lucide-react";
import { useEffect, useState } from "react";

import { useCreateShopifyRecipe, useShopifyStores } from "@design-lab/features/shopify-recipe";
import { useValidation } from "@design-lab/shared/hooks/useValidation";
import { Button } from "@design-lab/shared/ui";
import { Select, SelectItem } from "@design-lab/shared/ui/design-system/Select";
import { shopifyRecipeLoadingAtom } from "@design-lab/shared/state";

type ShopifyRecipeButtonProps = {
  isAnyLoading: boolean;
  isDesktop: boolean;
};

export function ShopifyRecipeButton({ isAnyLoading, isDesktop }: ShopifyRecipeButtonProps) {
  const { stores, isLoading: isLoadingStores } = useShopifyStores();
  const { createShopifyRecipe, isLoading: isCreatingShopifyRecipe } = useCreateShopifyRecipe();
  const [isShopifyRecipeLoading] = useAtom(shopifyRecipeLoadingAtom);
  const [selectedStoreId, setSelectedStoreId] = useState<string>("");
  const validation = useValidation();

  useEffect(() => {
    const firstStore = stores[0];
    if (firstStore && !selectedStoreId) {
      setSelectedStoreId(firstStore.id);
    }
  }, [stores, selectedStoreId]);

  const handleCreateShopifyRecipe = async () => {
    if (!selectedStoreId) return;
    await createShopifyRecipe(selectedStoreId);
  };

  const hasNoStores = !isLoadingStores && stores.length === 0;

  const isDisabled =
    isAnyLoading ||
    isShopifyRecipeLoading ||
    hasNoStores ||
    !selectedStoreId ||
    !validation.isArtworkUploaded ||
    validation.isSomeArtworkLoading;

  const noStoresMessage = "You are not an admin of any Shopify store";
  const tooltipTitle = hasNoStores ? noStoresMessage : undefined;

  return (
    <>
      <div
        className={
          hasNoStores ? "order-3 cursor-not-allowed lg:order-none" : "order-3 lg:order-none"
        }
        title={tooltipTitle}
      >
        <Select
          data-testid="shopify-store-select"
          aria-label="Select store"
          placeholder="Select Store"
          selectedKey={selectedStoreId}
          onSelectionChange={key => setSelectedStoreId(key == null ? "" : String(key))}
          isDisabled={isAnyLoading || isLoadingStores || hasNoStores}
          className="h-12 w-40 lg:h-12 xl:h-16 [&_button]:h-full [&_button]:border-green-600"
          items={stores}
          prefix={<Building2 className="size-5" />}
        >
          {store => (
            <SelectItem id={store.id} data-testid="shopify-store-option">
              {store.name}
            </SelectItem>
          )}
        </Select>
      </div>
      <div
        className={
          hasNoStores ? "order-4 cursor-not-allowed lg:order-none" : "order-4 lg:order-none"
        }
        title={tooltipTitle}
      >
        <Button
          data-testid="create-shopify-recipe-button"
          onPress={handleCreateShopifyRecipe}
          variant="outline"
          size={isDesktop ? "3xl" : "xl"}
          className="w-full border border-green-600 text-green-600 hover:bg-green-50 lg:h-12 lg:w-auto lg:min-w-48 xl:h-16"
          isDisabled={isDisabled}
        >
          {isCreatingShopifyRecipe ? (
            <Loader2 className="size-5 animate-spin" />
          ) : (
            <Store className="size-5" />
          )}
          {isCreatingShopifyRecipe ? "Creating..." : "Create Shopify Recipe"}
        </Button>
      </div>
    </>
  );
}
